"use client"

import type { ReactNode } from "react"
import React, { forwardRef, memo } from "react"

import { cn } from "@productlane/lib/src/browser"

import type { DialogPosition, DialogSize } from "./internal/dialog"
import type { DialogContentProps } from "@radix-ui/react-dialog"
import {
	InternalDialog,
	InternalDialogContent,
	InternalDialogDescription,
	InternalDialogFooter,
	InternalDialogHeader,
	InternalDialogTitle,
	InternalDialogTrigger,
} from "./internal/dialog"

export interface DialogProps {
	isOpen: boolean
	onOpenChange: (value: boolean) => void
	trigger?: ReactNode
	title?: ReactNode
	description?: ReactNode
	footer?: ReactNode
	header?: ReactNode
	children: ReactNode
	size?: DialogSize
	position?: DialogPosition
	showCloseButton?: boolean
	className?: string
	footerClassName?: string
	scrollContainerClasses?: string
	contentPadding?: string
	withOverlay?: boolean
	darkOverlay?: boolean
	modal?: boolean
	onCloseAutoFocus?: DialogContentProps["onCloseAutoFocus"]
	onOpenAutoFocus?: DialogContentProps["onOpenAutoFocus"]
	onInteractOutside?: DialogContentProps["onInteractOutside"]
}

export const Dialog = memo(
	forwardRef<React.ElementRef<typeof InternalDialogContent>, DialogProps>(
		(
			{
				trigger,
				title,
				description,
				header,
				footer,
				children,
				isOpen,
				onOpenChange,
				onCloseAutoFocus,
				onOpenAutoFocus,
				onInteractOutside,
				size,
				position = "center",
				showCloseButton = true,
				className,
				footerClassName,
				scrollContainerClasses,
				contentPadding,
				withOverlay,
				darkOverlay,
				modal = true,
			},
			ref,
		) => {
			return (
				<ModalStateProvider
					value={{
						onClose: () => {
							onOpenChange?.(false)
						},
					}}
				>
					<InternalDialog
						open={isOpen}
						onOpenChange={onOpenChange}
						modal={modal}
					>
						{trigger && (
							<InternalDialogTrigger asChild>{trigger}</InternalDialogTrigger>
						)}
						<InternalDialogContent
							onCloseAutoFocus={onCloseAutoFocus}
							onOpenAutoFocus={onOpenAutoFocus}
							onInteractOutside={onInteractOutside}
							ref={ref}
							size={size}
							position={position}
							showCloseButton={showCloseButton}
							className={className}
							contentPadding={contentPadding}
							withOverlay={withOverlay}
							darkOverlay={darkOverlay}
							isOpen={isOpen}
						>
							{!!title || !!description ? (
								<InternalDialogHeader>
									{title ? (
										<InternalDialogTitle>{title}</InternalDialogTitle>
									) : null}
									{description ? (
										<InternalDialogDescription asChild>
											<div>{description}</div>
										</InternalDialogDescription>
									) : null}
									{header}
								</InternalDialogHeader>
							) : null}
							<div
								className={cn(
									"-mx-1 min-h-0 flex-1 overflow-y-auto px-[5px] py-1.5",
									scrollContainerClasses,
								)}
							>
								{children}
							</div>
							{footer ? (
								<InternalDialogFooter className={footerClassName}>
									{footer}
								</InternalDialogFooter>
							) : null}
						</InternalDialogContent>
					</InternalDialog>
				</ModalStateProvider>
			)
		},
	),
)

export interface ModalStateContext {
	onClose: () => void
}

export const ModalStateContext = React.createContext<ModalStateContext | null>(
	null,
)

export function ModalStateProvider({
	value,
	children,
}: React.PropsWithChildren<{ value: ModalStateContext }>) {
	return (
		<ModalStateContext.Provider
			value={{
				...value,
			}}
		>
			{children}
		</ModalStateContext.Provider>
	)
}
export const useModalContext = () => {
	const context = React.useContext(ModalStateContext)
	if (!context) {
		throw Error("Wrap your modal with `<ModalStateProvider />`")
	} else {
		return context
	}
}

export const useOptionalModalContext = () => {
	const context = React.useContext(ModalStateContext)
	return context
}
